import { effectScope, reactive } from "vue"
import {
  useMutation as useVueMutation,
  useQuery as useVueQuery,
  QueryClient,
  QueryCache,
  Query,
} from "@tanstack/vue-query"
import {
  QueryResult,
  QueryArgs,
  MutationResult,
  MutationArgs,
  ApiError,
} from "./types"
import errorHandler from "@/services/ErrorService/errorHandler"
import Cache from "./Cache"
import { isObject } from "lodash"

const queryReferences = reactive({})
/**
 * @param placeholderData - Initial value used before queryFn resolves. It is not persisted in cache.
 * @param parserFn - Function transforming result returned from queryFn.
 * @param observerKey - Distinguishes observers of the same queryKey (e.g. with
 * different `select` functions), which would otherwise share one reference.
 * @param showSnackbarError - Shows a failed query in the snackbar; otherwise it is only reported.
 */

export const handleQueryError = (
  error: unknown,
  query?: Pick<Query, "meta">,
) => {
  if (!query?.meta?.showSnackbarError) {
    errorHandler.reportApiError(error as ApiError)
    return
  }

  const errorMessage = query.meta.errorMessage?.toString()
  errorHandler.handleApiError(error as ApiError, errorMessage)
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      handleQueryError(error, query)
      throw error
    },
  }),
})

export const useQuery = <TRaw, TParsed>({
  queryKey,
  queryFn,
  parserFn,
  placeholderData = {} as TParsed,
  useRawResponse,
  disableCache,
  observerKey,
  showSnackbarError = false,
  ...vueQueryArgs
}: QueryArgs<TRaw, TParsed>): QueryResult<TParsed> => {
  const index = observerKey
    ? `${String(queryKey)}:${observerKey}`
    : String(queryKey)
  if (!queryReferences[index]) {
    const scope = effectScope(true)

    scope.run(() => {
      const queryAndParseFn = async () => {
        const response = await queryFn()

        if (useRawResponse) return parserFn(response as TRaw)
        if (!isObject(response) || !("data" in response)) {
          throw new Error(
            "No 'data' property in the response object. Missing 'useRawResponse' flag?",
          )
        }

        return parserFn(response.data)
      }

      queryReferences[index] = useVueQuery(
        {
          queryKey: disableCache ? [] : queryKey,
          gcTime: disableCache ? 0 : undefined,
          placeholderData: placeholderData as any,
          queryFn: queryAndParseFn,
          refetchOnWindowFocus: false,
          retry: false,
          ...vueQueryArgs,
          meta: { ...vueQueryArgs.meta, showSnackbarError },
        },
        queryClient,
      )
    })
  }

  return queryReferences[index]
}

/**
 * @param mutationFn
 * @param serializerFn - Function transforming the data before it's passed to mutation function
 * @param errorHandlerFn - Function allowing to define custom error handling.
 * The thrown error will be available on mutationResult.error
 * @param onSuccessFn - Function executed after mutation has been submitted successfully
 * @param onErrorFn - Function executed after mutation has been submitted unsuccessfully
 * @param invalidateCache
 */
export const useMutation = <TVariables, TSerialized, TResponse>({
  mutationFn,
  serializerFn,
  errorHandlerFn,
  onSuccessFn,
  onErrorFn,
  invalidateCache = true,
}: MutationArgs<TVariables, TSerialized, TResponse>): MutationResult<
  TVariables,
  TResponse
> => {
  const newMutationFn = (variables: TVariables) => {
    try {
      return mutationFn(serializerFn ? serializerFn(variables) : variables)
    } catch (error) {
      if (errorHandlerFn) {
        errorHandlerFn(error, variables)
      } else {
        throw error
      }
    }
  }

  const vueMutationResult = useVueMutation(
    {
      mutationFn: newMutationFn,
      onError: (error: ApiError) => {
        errorHandler.reportApiError(error)
        onErrorFn?.(error)
      },
      onSuccess: (response, variables) => {
        if (onSuccessFn) {
          onSuccessFn(response as TResponse, variables)
        } else {
          invalidateCache && Cache.clear()
        }
      },
    },
    queryClient,
  )

  const { mutateAsync, reset, data, variables, ...rest } = vueMutationResult

  // Nobody can catch a rejection from mutate(), so the snackbar has to happen
  // here. mutateAsync() rejections are the caller's to show; one that escapes
  // is reported to Datadog but stays silent unless it is a 403 (see the
  // unhandledApiErrors plugin).
  return {
    ...rest,
    mutate: (variables?: TVariables) => {
      mutateAsync(variables as TVariables).catch((error: ApiError) =>
        errorHandler.showApiError(error),
      )
    },
    mutateAsync: (variables?: TVariables) =>
      mutateAsync(variables as TVariables),
    reset,
    data: data as TResponse,
    variables: variables as TVariables | undefined,
  }
}
